import { ArrowDownRight, ArrowUpRight, type LucideIcon } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/* KpiCard — guide §5: the dashboard signature card. Caption label, big
   tabular number, optional trend, optional colored icon medallion. */

export interface KpiTrend {
  direction: "up" | "down" | "flat";
  value: string;
  context?: string;
}

/** Pre-baked colour pairings for the icon medallion. Defaults to "blue". */
export type KpiTone = "blue" | "cyan" | "green" | "purple" | "amber";

const TONE_CLASS: Record<KpiTone, string> = {
  blue: "bg-blue/10 text-blue",
  cyan: "bg-cyan/10 text-cyan",
  green: "bg-green/10 text-green",
  purple: "bg-purple/10 text-purple",
  amber: "bg-amber-50 text-amber-600",
};

export interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  icon?: LucideIcon;
  iconTone?: KpiTone;
  trend?: KpiTrend;
  loading?: boolean;
  /** The backing query failed (e.g. forbidden / network). Renders an
   *  honest "Unavailable" instead of a silent dash that reads like a
   *  real value. Ignored while {@link loading}. */
  error?: boolean;
  to?: string;
  onClick?: () => void;
  className?: string;
  /** When false the trend treats "up" as bad (e.g. attrition). Default true. */
  positiveIsGood?: boolean;
}

export function KpiCard({
  label,
  value,
  icon: Icon,
  iconTone = "blue",
  trend,
  loading,
  error,
  className,
  positiveIsGood = true,
}: KpiCardProps) {
  const trendColor = trend
    ? trend.direction === "flat"
      ? "text-slate"
      : (trend.direction === "up") === positiveIsGood
        ? "text-[#047857]"
        : "text-[#B91C1C]"
    : undefined;

  return (
    <Card
      className={cn(
        "group relative overflow-hidden rounded-card border-slate-200 bg-white p-5 shadow-card transition-shadow hover:shadow-card-lg",
        className,
      )}
    >
      <div className="flex items-center gap-4">
        {Icon && (
          <span
            className={cn(
              "flex h-14 w-14 shrink-0 items-center justify-center rounded-full transition-transform group-hover:scale-105",
              TONE_CLASS[iconTone],
            )}
            aria-hidden="true"
          >
            <Icon size={24} strokeWidth={1.75} />
          </span>
        )}

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate">
            {label}
          </p>

          {loading ? (
            <Skeleton className="mt-2 h-8 w-24" />
          ) : error ? (
            <p
              className="mt-2 text-sm font-medium text-slate"
              title="Couldn't load this metric"
            >
              Unavailable
            </p>
          ) : (
            <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-navy tabular-nums">
              {value}
            </p>
          )}

          {trend && !loading && !error && (
            <p
              className={cn(
                "mt-1 inline-flex items-center gap-1 text-xs",
                trendColor,
              )}
            >
              {trend.direction === "up" ? (
                <ArrowUpRight size={13} strokeWidth={2.25} aria-hidden="true" />
              ) : trend.direction === "down" ? (
                <ArrowDownRight size={13} strokeWidth={2.25} aria-hidden="true" />
              ) : null}
              <span className="font-semibold tabular-nums">{trend.value}</span>
              {trend.context && (
                <span className="text-slate">{trend.context}</span>
              )}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
